import { useLayoutEffect } from 'react';

/**
 * Follow the playback position every animation frame while playing.
 *
 * `getSec` reports a continuous position (the playback engine extrapolates
 * between its own clock steps), so callers just follow it frame by frame — no
 * interpolation here, and no re-render: nothing that moves with playback needs
 * React state. Used by the playhead and the toolbar clock.
 *
 * While paused there is nothing to follow — `sec` is drawn as-is.
 *
 * `draw(sec)` should write straight to the DOM. Pass it memoised: the effect
 * re-runs when it changes, which is also what re-draws after a zoom.
 */
const useLiveTime = (sec, playing, draw, getSec) => {
  const follow = playing && !!getSec;
  // While following, `sec` is unused — hold it constant so a new report doesn't
  // tear down and restart the loop several times a second.
  const staticSec = follow ? 0 : sec;

  useLayoutEffect(() => {
    if (!follow) {
      draw(staticSec);
      return undefined;
    }

    let rafId = 0;
    const tick = () => {
      draw(getSec());
      rafId = window.requestAnimationFrame(tick);
    };
    tick();

    return () => window.cancelAnimationFrame(rafId);
  }, [follow, staticSec, draw, getSec]);
};

export default useLiveTime;
